// apps/web/src/features/auth/pages/RegisterPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePageScale } from "@/ui/usePageScale";
import { useLocation, useNavigate } from "react-router-dom";
import { customConfirm } from "@/ui/confirm";
import { PageShell } from "@/ui/PageShell";
import { withBase } from "@/ui/Header";
import { COLOR, FW, BORDER } from "@/ui/tokens";

const ICONS = {
    showPw: withBase("icons/show_pw.svg"),
    hidePw: withBase("icons/hide_pw.svg"),
};

const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE ?? import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");


const BASE = {
    W: 1264,
    GAP_HDR_TO_FIRST: 24,
    MARGIN_X: 108,

    INPUT_SM_W: 724,
    ACTION_W: 299,
    INPUT_H: 124,
    ACTION_H: 124,
    INPUT_FZ: 35,

    INPUT_LG_W: 1048,
    INPUT_LG_H: 124,
    ROW_GAP_INPUTS: 40,

    SUBMIT_W: 724,
    SUBMIT_H: 124,
    SUBMIT_FZ: 40,
    GAP_LAST_TO_SUBMIT: 80,
} as const;

function useScale() {
    const { s, px, sfpx } = usePageScale({ baseW: BASE.W, max: 1.2 });
    const spx = px;
    return { s, spx, sfpx };
}

type Agreements = {
    terms_version: string;
    privacy_version: string;
    marketing_sms?: boolean;
};

type ServerError = { error?: string; message?: string; fields?: string[] };

export default function RegisterPage() {
    const { s, spx, sfpx } = useScale();
    const nav = useNavigate();
    const loc = useLocation() as any;

    const [ready, setReady] = useState(false);
    const [agreements, setAgreements] = useState<Agreements | null>(null);

    // 요구사항: 아이디 / 비밀번호 / 비밀번호 확인 3개만 노출
    const [username, setUsername] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [showPw2, setShowPw2] = useState(false);

    // 아이디 중복확인 상태
    const [usernameChecked, setUsernameChecked] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    // 아이디 힌트(입력창 아래 표시)
    const [usernameHint, setUsernameHint] =
        useState<{ type: "ok" | "err"; text: string } | null>(null);

    // 휴대폰 인증 상태
    const [phoneSessionId, setPhoneSessionId] = useState<string | null>(null);
    const [phoneProof, setPhoneProof] = useState<string | null>(null);
    const [phoneE164, setPhoneE164] = useState<string | null>(null);

    // phoneProof stale 방지 (popup close 감지에서 사용)
    const phoneProofRef = useRef<string | null>(null);
    useEffect(() => {
        phoneProofRef.current = phoneProof;
    }, [phoneProof]);

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);
    const [showLoginCta, setShowLoginCta] = useState(false);

    // stale closure 방지
    const phoneSessionRef = useRef<string | null>(null);

    const confirmInFlightRef = useRef(false);
    const confirmedRef = useRef(false);

    useEffect(() => {
        confirmedRef.current = !!phoneProof;
    }, [phoneProof]);

    useEffect(() => {
        phoneSessionRef.current = phoneSessionId;
    }, [phoneSessionId]);

    useEffect(() => {
        let ag: Agreements | null = null;
        const st: Agreements | undefined = loc.state?.agreements;
        if (st) ag = st;
        else {
            try {
                ag = JSON.parse(sessionStorage.getItem("bt.agreements") || "null");
            } catch {}
        }
        if (!ag?.terms_version || !ag?.privacy_version) {
            nav("/auth/agree", { replace: true });
            return;
        }
        setAgreements(ag);
        setReady(true);
    }, [loc.state, nav]);

    // 정책: 아이디 영문/숫자 10자
    const usernameOk = useMemo(() => /^[A-Za-z0-9]{1,10}$/.test(username.trim()), [username]);
    // 비번 정책: 4~20자, 영문(대소문자)+숫자 포함, 특수문자 허용
    const pwPolicyOk = useMemo(() => /^(?=.*[A-Za-z])(?=.*\d).{4,20}$/.test(pw), [pw]);
    const pwMatchOk = useMemo(() => pw2 === pw && pw.length > 0, [pw, pw2]);

    const pw2Touched = useMemo(() => pw2.length > 0, [pw2]);

    const pwMatchText = useMemo(() => {
        if (!pw2Touched) return null;
        if (pw2 !== pw) return { type: "err", text: "비밀번호가 일치하지 않습니다." };
        if (!pwPolicyOk) return null; // match but policy not met — suppress ok message
        return { type: "ok", text: "비밀번호가 일치합니다." };
    }, [pw, pw2, pw2Touched, pwPolicyOk]);

    const canStartVerification =
        usernameOk && pwPolicyOk && pwMatchOk && usernameChecked && usernameAvailable === true && !phoneProof;

    const canRegister =
        usernameOk && pwPolicyOk && pwMatchOk && usernameChecked && usernameAvailable === true && !!phoneProof;

    const [pwPolicyTried, setPwPolicyTried] = useState(false);
    const showPwPolicyWarning = pwPolicyTried && !pwPolicyOk;
    const showDupCheckReminder = pw.length > 0 && !usernameChecked && !usernameHint;

    // username 변경 시 중복확인 상태 초기화
    useEffect(() => {
        setUsernameChecked(false);
        setUsernameAvailable(null);
        setUsernameHint(null);
        setShowLoginCta(false);
    }, [username]);

    // 팝업 -> 부모창 메시지 수신(콜백 HTML에서 postMessage)
    useEffect(() => {
        const onMessage = (ev: MessageEvent) => {
            const d: any = ev.data;
            if (!d || d.type !== "KMC_DONE") return;

            // session_id가 온 경우, 현재 세션과 다르면 무시(보수적으로)
            const sid = String(d.session_id || "");
            const cur = phoneSessionRef.current;
            if (sid && cur && sid !== cur) return;

            // 자동 confirm (실패해도 사용자가 다시 시도 가능)
            void confirmPhoneVerification();
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showError = (text: string) => {
        setErr(text);
        setMsg(null);
    };

    const showMessage = (text: string) => {
        setMsg(text);
        setErr(null);
    };

    const checkUsername = async () => {
        setErr(null);
        setMsg(null);
        setShowLoginCta(false);
        setUsernameHint(null);

        const u = username.trim();
        if (!u) return;
        if (!usernameOk) {
            setUsernameHint({ type: "err", text: "아이디는 영문/숫자만, 최대 10자입니다." });
            return;
        }

        setBusy(true);
        try {
            const r = await fetch(`${AUTH_BASE}/username/available?username=${encodeURIComponent(u)}`);
            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) throw new Error(`HTTP ${r.status}`);

            const ok = !!data?.available;
            setUsernameChecked(true);
            setUsernameAvailable(ok);

            if (ok) setUsernameHint({ type: "ok", text: "사용 가능한 아이디입니다." });
            else setUsernameHint({ type: "err", text: "이미 사용 중인 아이디입니다." });
        } catch {
            setUsernameHint({ type: "err", text: "아이디 중복확인 실패" });
        } finally {
            setBusy(false);
        }
    };

    const startPhoneVerification = async () => {
        setErr(null);
        setMsg(null);
        setPhoneProof(null);
        setPhoneE164(null);
        setShowLoginCta(false);

        if (!canStartVerification) {
            // 유저가 왜 비활성인지 빠르게 파악 가능하도록 메시지 제공
            if (!usernameOk) return showError("아이디 형식을 확인해주세요.");
            if (!pwPolicyOk) return showError("비밀번호는 4~20자, 영문+숫자를 포함해야 합니다.");
            if (!pwMatchOk) return showError("비밀번호가 일치하지 않습니다.");
            if (!usernameChecked) return showError("아이디 중복확인을 먼저 진행해주세요.");
            if (usernameAvailable === false) return showError("이미 사용 중인 아이디입니다.");
            return;
        }

        // 팝업 차단 방지: 클릭 순간에 먼저 open
        const popup = window.open(
            "",
            "KMCISWindow",
            "width=420,height=720,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes"
        );

        if (!popup) {
            showError("팝업이 차단되었습니다. 브라우저에서 팝업 허용 후 다시 시도해주세요.");
            return;
        }

        popup.document.write(
            "<p style='font-family:sans-serif;padding:16px'>본인인증 준비 중...</p>"
        );

        setBusy(true);
        try {
            const r = await fetch(`${AUTH_BASE}/phone/start`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ return_url: "" }),
            });
            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                throw new Error(data?.error || `HTTP ${r.status}`);
            }

            const sessionId: string = data.session_id;
            const kmc = data.kmc || {};
            setPhoneSessionId(sessionId);

            // KMC로 form POST (샘플 기준: tr_cert / tr_url / tr_ver만)
            const form = popup.document.createElement("form");
            form.method = "POST";
            form.action = kmc.action;
            form.target = "KMCISWindow";

            const add = (k: string, v: string) => {
                const input = popup.document.createElement("input");
                input.type = "hidden";
                input.name = k;
                input.value = v ?? "";
                form.appendChild(input);
            };

            add("tr_cert", kmc.tr_cert || "");
            add("tr_url", kmc.tr_url || "");
            add("tr_ver", kmc.tr_ver || "V2");

            popup.document.body.appendChild(form);
            form.referrerPolicy = "no-referrer";
            form.submit();

            showMessage("휴대폰 인증을 완료해주세요.");

            // 사용자가 팝업을 직접 닫는 케이스 감지(취소 UX)
            const startedAt = Date.now();
            const timer = window.setInterval(() => {
                if (popup.closed) {
                    window.clearInterval(timer);
                    // 인증 완료(postMessage)로 phoneProof가 생겼으면 메시지 유지
                    if (!phoneSessionRef.current) return;
                    if (!phoneProofRef.current) {
                        showError("인증이 취소되었거나 중단되었습니다. 다시 시도해주세요.");
                    }

                }
                // 10분 초과 시 UX 안내
                if (Date.now() - startedAt > 10 * 60 * 1000 && !phoneProofRef.current) {
                    window.clearInterval(timer);
                    showError("인증 시간이 만료되었습니다. 다시 시도해주세요.");
                }
            }, 600);
        } catch (e: any) {
            try {
                popup.close();
            } catch {}
            showError(e?.message || "휴대폰 인증 시작 실패");
        } finally {
            setBusy(false);
        }
    };

    const confirmPhoneVerification = async () => {
        const sid = phoneSessionRef.current;
        if (!sid) return;

        // 이미 완료면 추가 호출 차단
        if (confirmedRef.current) return;

        // 이미 진행 중이면 추가 호출 차단
        if (confirmInFlightRef.current) return;
        confirmInFlightRef.current = true;

        setBusy(true);
        setErr(null);
        setMsg(null);
        setShowLoginCta(false);

        try {
            const r = await fetch(`${AUTH_BASE}/phone/confirm`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ session_id: sid }),
            });

            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                const code = String((data as ServerError)?.error || "");
                if (code === "SESSION_EXPIRED") {
                    showError("인증 시간이 만료되었습니다. 다시 시도해주세요.");
                    return;
                }
                showError("인증 확인에 실패했습니다. 다시 시도해주세요.");
                return;
            }

            setPhoneProof(data.phone_proof || null);
            setPhoneE164(data.phone_e164 || null);
            showMessage("휴대폰 인증이 완료되었습니다. 아래 ‘회원가입 완료’를 눌러 가입을 마무리해주세요.");
        } catch {
            showError("인증 확인에 실패했습니다. 다시 시도해주세요.");
        } finally {
            confirmInFlightRef.current = false;
            setBusy(false);
        }
    };

    const submitRegister = async () => {
        if (!agreements) return;
        if (!canRegister) return;

        setBusy(true);
        setErr(null);
        setMsg(null);
        setShowLoginCta(false);

        try {
            const r = await fetch(`${AUTH_BASE}/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    username: username.trim(),
                    password: pw,
                    phone_proof: phoneProof,
                    agreements,
                }),
            });

            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                const code = String((data as ServerError)?.error || "");
                if (code === "PHONE_TAKEN" || code === "DI_TAKEN") {
                    setShowLoginCta(true);
                    showError("이미 가입된 휴대폰 번호입니다.");
                    return;
                }
                if (code === "USERNAME_TAKEN") {
                    showError("이미 사용 중인 아이디입니다.");
                    setUsernameAvailable(false);
                    setUsernameChecked(true);
                    return;
                }
                if (code === "PHONE_VERIFICATION_REQUIRED") {
                    showError("휴대폰 인증이 필요합니다.");
                    setPhoneProof(null);
                    return;
                }

                showError(data?.message || "회원가입에 실패했습니다.");
                return;
            }

            sessionStorage.removeItem("bt.agreements");
            if (loc.state?.fromEvent) {
                await customConfirm("회원가입이 완료되었습니다. InnoSpaceOne로 체험 해보세요.", "확인");
                window.close();
            } else {
                showMessage("회원가입이 완료되었습니다. 로그인해주세요.");
                nav("/auth/login", { replace: true });
            }
        } catch {
            showError("회원가입에 실패했습니다.");
        } finally {
            setBusy(false);
        }
    };

    const onPrimaryClick = async () => {
        if (!phoneProof) {
            await startPhoneVerification();
            return;
        }
        await submitRegister();
    };

    if (!ready || !agreements) return null;

    const Label = ({ children }: { children: React.ReactNode }) => (
        <div style={{ fontSize: sfpx(36), fontWeight: FW.extraBold, color: COLOR.text, marginBottom: spx(12) }}>{children}</div>
    );

    const primaryLabel = phoneProof ? "회원가입 완료" : "인증하기";
    const primaryDisabled = busy || (phoneProof ? !canRegister : !canStartVerification);

    return (
        <PageShell
            title="회원가입"
            onBack={() => nav(-1)}
            backIcon={withBase("icons/prev1.svg")}
            width={spx(BASE.W)}
            bodyStyle={{ paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}
        >
            <div style={{ height: spx(BASE.GAP_HDR_TO_FIRST) }} />

            {/* 아이디 */}
            <Label>아이디</Label>

            <div style={{ position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <input
                        placeholder="아이디 입력"
                        inputMode="text"
                        autoComplete="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        style={{
                            width: spx(BASE.INPUT_SM_W),
                            height: spx(BASE.INPUT_H),
                            borderRadius: s(12),
                            padding: `0 ${spx(16)}`,
                            border: BORDER.boldDark,
                            fontSize: sfpx(BASE.INPUT_FZ),
                            fontWeight: FW.extraBold,
                        }}
                    />
                    <button
                        type="button"
                        onClick={checkUsername}
                        disabled={busy || !username.trim()}
                        style={{
                            marginLeft: spx(25),
                            width: spx(BASE.ACTION_W),
                            height: spx(BASE.ACTION_H),
                            borderRadius: s(12),
                            background: busy || !username.trim() ? "#C8C8C8" : "#8C8C8C",
                            color: COLOR.white,
                            fontWeight: FW.extraBold,
                            fontSize: sfpx(BASE.INPUT_FZ),
                            border: "none",
                        }}
                    >
                        중복확인
                    </button>
                </div>

                {/* 아이디 힌트: 레이아웃에 영향 없이 오버레이 */}
                {usernameHint && (
                    <div
                        style={{
                            position: "absolute",
                            left: 0,
                            top: spx(BASE.INPUT_H) + spx(10),
                            fontSize: sfpx(20),
                            fontWeight: FW.extraBold,
                            color: usernameHint.type === "err" ? COLOR.error : COLOR.success,
                            lineHeight: 1.2,
                            pointerEvents: "none",
                        }}
                    >
                        {usernameHint.text}
                    </div>
                )}
                {showDupCheckReminder && (
                    <div
                        style={{
                            position: "absolute",
                            left: 0,
                            top: spx(BASE.INPUT_H) + spx(10),
                            fontSize: sfpx(20),
                            fontWeight: FW.extraBold,
                            color: COLOR.warning,
                            lineHeight: 1.2,
                            pointerEvents: "none",
                        }}
                    >
                        아이디 중복확인을 먼저 진행해주세요.
                    </div>
                )}
            </div>

            <div style={{ height: spx(BASE.ROW_GAP_INPUTS) }} />

            {/* 비밀번호 */}
            <Label>비밀번호</Label>

            <div style={{ position: "relative" }}>
                <input
                    placeholder="비밀번호 입력"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    onBlur={() => { if (pw.length > 0 && !pwPolicyOk) setPwPolicyTried(true); }}
                    style={{
                        width: spx(BASE.INPUT_LG_W),
                        height: spx(BASE.INPUT_LG_H),
                        borderRadius: s(12),
                        padding: `0 ${spx(120)} 0 ${spx(16)}`,
                        border: BORDER.boldDark,
                        fontSize: sfpx(BASE.INPUT_FZ),
                        fontWeight: FW.extraBold,
                        boxSizing: "border-box",
                    }}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPw(v => !v)}
                    style={{
                        position: "absolute",
                        right: spx(16),
                        top: spx(BASE.INPUT_LG_H / 2),
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        lineHeight: 0,
                    }}
                >
                    <img src={showPw ? ICONS.hidePw : ICONS.showPw} alt={showPw ? "숨기기" : "보기"} style={{ width: spx(48), height: spx(48), display: "block" }} />
                </button>

                <div style={{ marginTop: spx(12), fontSize: sfpx(20), color: showPwPolicyWarning ? COLOR.error : COLOR.text }}>
                    4~20자 / 영문+숫자 포함 / 특수문자 사용 가능
                </div>
                {showPwPolicyWarning && (
                    <div style={{ marginTop: spx(4), fontSize: sfpx(20), fontWeight: FW.extraBold, color: COLOR.error }}>
                        영문자와 숫자를 각각 하나 이상 입력해주세요.
                    </div>
                )}

                {/* 비번 일치/불일치: 레이아웃에 영향 없이 오버레이 */}
                {pwMatchText && (
                    <div
                        style={{
                            position: "absolute",
                            left: 0,
                            top:
                                spx(BASE.INPUT_LG_H) + // input 아래
                                spx(12) +             // 정책문구 marginTop
                                spx(24) +             // 정책문구 한 줄 높이(대략)
                                spx(6),               // 여백
                            fontSize: sfpx(20),
                            fontWeight: FW.extraBold,
                            color: pwMatchText.type === "err" ? COLOR.error : COLOR.success,
                            lineHeight: 1.2,
                            pointerEvents: "none",
                        }}
                    >
                        {pwMatchText.text}
                    </div>
                )}
            </div>

            <div style={{ height: spx(BASE.ROW_GAP_INPUTS) }} />

            {/* 비밀번호 확인 */}
            <Label>비밀번호 확인</Label>
            <div style={{ position: "relative", display: "inline-block" }}>
                <input
                    placeholder="비밀번호 확인"
                    type={showPw2 ? "text" : "password"}
                    autoComplete="new-password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    style={{
                        width: spx(BASE.INPUT_LG_W),
                        height: spx(BASE.INPUT_LG_H),
                        borderRadius: s(12),
                        padding: `0 ${spx(120)} 0 ${spx(16)}`,
                        border: BORDER.boldDark,
                        fontSize: sfpx(BASE.INPUT_FZ),
                        fontWeight: FW.extraBold,
                        boxSizing: "border-box",
                    }}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPw2(v => !v)}
                    style={{
                        position: "absolute",
                        right: spx(16),
                        top: spx(BASE.INPUT_LG_H / 2),
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        lineHeight: 0,
                    }}
                >
                    <img src={showPw2 ? ICONS.hidePw : ICONS.showPw} alt={showPw2 ? "숨기기" : "보기"} style={{ width: spx(48), height: spx(48), display: "block" }} />
                </button>
            </div>

            {(err || msg) && (
                <>
                    <div style={{ height: spx(16) }} />
                    <div role="status" style={{ fontSize: sfpx(20), color: err ? COLOR.error : COLOR.success }}>
                        {err || msg}
                    </div>
                </>
            )}

            {/*{phoneProof && (*/}
            {/*    <>*/}
            {/*        <div style={{ height: spx(12) }} />*/}
            {/*        <div style={{ fontSize: sfpx(18), color: "#111" }}>인증된 번호: {phoneE164 || "-"}</div>*/}
            {/*    </>*/}
            {/*)}*/}

            {/* 자동 confirm 실패 시를 위한 안전장치(숨김 수준) */}
            {!phoneProof && phoneSessionId && (
                <>
                    <div style={{ height: spx(12) }} />
                    <button
                        type="button"
                        onClick={confirmPhoneVerification}
                        disabled={busy || confirmInFlightRef.current}
                        style={{
                            background: "transparent",
                            border: "none",
                            textDecoration: "underline",
                            fontSize: sfpx(20),
                            fontWeight: FW.extraBold,
                            color: COLOR.text,
                            padding: 0,
                        }}
                    >
                        인증 완료 확인
                    </button>
                </>
            )}

            <div style={{ height: spx(BASE.GAP_LAST_TO_SUBMIT) }} />
            <div style={{ display: "grid", placeItems: "center" }}>
                <button
                    type="button"
                    onClick={onPrimaryClick}
                    disabled={primaryDisabled}
                    style={{
                        width: spx(BASE.SUBMIT_W),
                        height: spx(BASE.SUBMIT_H),
                        borderRadius: s(12),
                        background: COLOR.black,
                        color: COLOR.white,
                        fontSize: sfpx(BASE.SUBMIT_FZ),
                        fontWeight: FW.extraBold,
                        border: "none",
                        opacity: primaryDisabled ? 0.75 : 1,
                    }}
                >
                    {busy ? "처리 중…" : primaryLabel}
                </button>
            </div>

            {showLoginCta && (
                <>
                    <div style={{ height: spx(20) }} />
                    <div style={{ display: "grid", placeItems: "center" }}>
                        <button
                            type="button"
                            onClick={() => nav("/auth/login", { replace: true })}
                            style={{
                                width: spx(BASE.SUBMIT_W),
                                height: spx(BASE.SUBMIT_H),
                                borderRadius: s(12),
                                background: COLOR.white,
                                color: COLOR.black,
                                fontSize: sfpx(BASE.SUBMIT_FZ),
                                fontWeight: FW.extraBold,
                                border: BORDER.boldDark,
                            }}
                        >
                            로그인으로 이동
                        </button>
                    </div>
                </>
            )}

            <div style={{ height: spx(40) }} />
        </PageShell>
    );
}
